import { defineStore } from "pinia";

import { isApiError } from "@/services/Api";
import { userSessionService } from "@/services";
import { SortBy, SortOrder } from "@/models/Filter";
import { ModuleStatus } from "@/models/ModuleStatus";
import { useBrandStore } from "@/stores/brand-store";
import { useLoggingStore } from "@/stores/logging-store";

import type { BrandSession, GlobalSession } from "@/models/UserSession";

interface State {
  moduleStatus: ModuleStatus;
  globalSession: GlobalSession;
  _brandSession: { name: string; data: BrandSession } | undefined;
}

const USER_SORT_DEFAULT: NonNullable<BrandSession["sort"]> = {
  sortBy: SortBy.FILE_NAME,
  sortOrder: SortOrder.ASC,
};

export const useUserSessionStore = defineStore("userSession", {
  state: (): State => ({
    moduleStatus: ModuleStatus.Initial,
    globalSession: {},
    _brandSession: undefined,
  }),
  getters: {
    brandLayout: (state) => {
      const brand = useBrandStore().selectedBrand;
      if (brand == null || state._brandSession?.name !== brand) return;
      return state._brandSession.data.layout;
    },
    brandSort: (state) => {
      const brand = useBrandStore().selectedBrand;
      if (brand == null || state._brandSession?.name !== brand) return USER_SORT_DEFAULT;
      return state._brandSession.data.sort ?? USER_SORT_DEFAULT;
    },
    brandLastUsedLens: (state) => {
      const brand = useBrandStore().selectedBrand;
      if (brand == null || state._brandSession?.name !== brand) return;
      return state._brandSession.data.lastUsedLens;
    },
  },
  actions: {
    async loadClientNameSession(): Promise<void> {
      const { selectedClient, selectedBrand } = useBrandStore();
      if (selectedBrand == null || selectedClient == null) return;

      this.setStatus(ModuleStatus.Loading);

      // Try and load the session into here
      try {
        // The api catches errors and logs them, so we don't need to do anything here
        const userSession = await userSessionService.getSession(selectedClient, selectedBrand);

        this.brandSessionLoaded(selectedBrand, userSession[selectedBrand]);
      } catch (error) {
        if (isApiError(error) && error.status === 204) {
          if (this._brandSession != null) this.brandSessionLoaded(selectedBrand, this._brandSession.data);
          return;
        }
        useLoggingStore().Error({
          message: "Failed to load user session into module",
          data: error as object,
        });
      } finally {
        this.setStatus(ModuleStatus.Ready);
      }
    },
    async loadGlobalSession(): Promise<void> {
      try {
        // The api catches errors and logs them, so we don't need to do anything here
        const session = await userSessionService.getSession<"global">(undefined, undefined);
        this.globalSessionLoaded(session.global);
      } catch (error) {
        console.error(error);
      }
    },
    async saveBrandSession(brandSession: BrandSession) {
      const { selectedClient, selectedBrand } = useBrandStore();
      if (selectedBrand == null || selectedClient == null) return;

      // Callers pass only the keys they manage (e.g. sort and layout), so merge over the loaded session
      // rather than dropping the rest, such as lastUsedLens.
      const current = this._brandSession?.name === selectedBrand ? this._brandSession.data : undefined;
      await userSessionService.setBrandSession(selectedClient, selectedBrand, { ...current, ...brandSession });
      await this.loadClientNameSession();
    },
    async saveLastUsedLens(lens: string): Promise<void> {
      await this.saveBrandSession({ lastUsedLens: lens });
    },
    async saveLastUsedBrand(brand: string): Promise<void> {
      const { mappings, selectedClient, selectedBrand } = useBrandStore();
      if (
        selectedBrand == null ||
        selectedClient == null ||
        Object.keys(mappings).find((key) => key.toLowerCase() === brand.toLowerCase()) == null
      )
        return;

      await userSessionService.setGlobalSession({ ...this.globalSession, lastUsedBrand: brand });
      await this.loadGlobalSession();
    },
    async resetBrandLensLayout() {
      const layout = this.brandLayout;
      if (layout == null) return;

      if (Object.keys(layout.lenses).length > 1) {
        // More than 1 specified lens layout, delete only the selected one
        const selectedLens = useBrandStore().selectedLens.code;

        await this.saveBrandSession({
          layout: {
            ...layout,
            lenses: { ...layout.lenses, [selectedLens]: undefined },
            closedPanelLastGroup: { ...layout.closedPanelLastGroup, [selectedLens]: undefined },
          },
        });
      } else {
        // Last lens, best to clear the entire brand layout
        await this.saveBrandSession({ layout: undefined });
      }

      await this.loadClientNameSession();
    },
    async reset() {
      const { selectedClient, selectedBrand } = useBrandStore();
      if (selectedBrand == null || selectedClient == null) return;

      await userSessionService.deleteUserSession(selectedClient, selectedBrand);
    },

    // Mutations
    setStatus(moduleStatus: ModuleStatus): void {
      this.moduleStatus = moduleStatus;
    },
    brandSessionLoaded(name: string, brandSession: BrandSession): void {
      const layout = brandSession.layout;

      if (layout?.provider !== "dockview" || !("lenses" in layout)) {
        brandSession.layout = {
          provider: "dockview",
          lenses: {
            [useBrandStore().selectedLens.code]:
              layout != null && "state" in layout ? (layout.state as string) : undefined,
          },
        };
      }

      this._brandSession = { name, data: brandSession };
    },
    globalSessionLoaded(data: GlobalSession): void {
      this.globalSession = data;
    },
  },
});
